// Pure utilities for leaderboard face calculations

import type { Emotion } from '@airhead-games/shared/configs';
import type { RoomPlayer, RoomTeam } from '@airhead-games/shared/types';

/**
 * Calculate leaderboard emotions based on player scores
 *
 * Assigns emotions to players based on their score ranking:
 * - Winners (highest scores) → surprised
 * - Losers (lowest scores) → angry
 * - Middle players → split into happy (top half) and sad (bottom half)
 * - All tied → happy
 *
 * @param players - Array of room players
 * @param scoreField - Which score field to use for ranking
 * @returns Map of playerId to emotion
 */
export function expressionForScore(
  players: RoomPlayer[],
  scoreField: 'scoreGame' | 'scoreTurn' = 'scoreGame'
): Map<string, Emotion> {
  const map = new Map<string, Emotion>();

  if (players.length === 0) return map;

  // Collect scores
  const scored = players.map((p) => ({
    id: p.playerId,
    score: scoreField === 'scoreTurn' ? (p.scoreTurn ?? 0) : (p.scoreGame ?? 0),
  }));

  // Determine extremes
  const scores = scored.map((s) => s.score);
  const maxScore = Math.max(...scores);
  const minScore = Math.min(...scores);

  // Everyone tied → all happy
  if (maxScore === minScore) {
    scored.forEach(({ id }) => map.set(id, 'happy'));
    return map;
  }

  // Top and bottom groups
  const topIds = new Set(
    scored.filter((s) => s.score === maxScore).map((s) => s.id)
  );
  const bottomIds = new Set(
    maxScore > minScore
      ? scored.filter((s) => s.score === minScore).map((s) => s.id)
      : []
  );

  // Assign top and bottom emotions
  topIds.forEach((id) => map.set(id, 'surprised'));
  bottomIds.forEach((id) => map.set(id, 'angry'));

  // Remaining players → split halves (prefer happy on odd)
  const remaining = scored
    .filter((s) => !topIds.has(s.id) && !bottomIds.has(s.id))
    .sort((a, b) => b.score - a.score);
  const n = remaining.length;
  const topHalfCount = Math.ceil(n / 2);
  remaining.forEach((s, idx) => {
    map.set(s.id, idx < topHalfCount ? 'happy' : 'sad');
  });

  return map;
}

/**
 * Calculate leaderboard emotions based on team scores
 *
 * Assigns emotions to players based on their team's score ranking:
 * - Winning teams (highest scores) → surprised
 * - Losing teams (lowest scores) → angry
 * - Middle teams → split into happy (top half) and sad (bottom half)
 * - All teams tied → all players show happy
 *
 * @param teams - Array of room teams
 * @param players - Array of room players
 * @param scoreField - Which score field to use for ranking ('scoreTurn' or 'scoreGame')
 * @returns Map of playerId to emotion
 */
export function expressionForTeamScore(
  teams: RoomTeam[],
  players: RoomPlayer[],
  scoreField: 'scoreGame' | 'scoreTurn' = 'scoreGame'
): Map<string, Emotion> {
  const map = new Map<string, Emotion>();

  if (teams.length === 0 || players.length === 0) return map;

  // Collect team scores
  const teamScores = teams.map((team) => ({
    teamId: team.teamId,
    score:
      scoreField === 'scoreTurn'
        ? (team.scoreTurn ?? 0)
        : (team.scoreGame ?? 0),
  }));

  // Determine extremes
  const scores = teamScores.map((t) => t.score);
  const maxScore = Math.max(...scores);
  const minScore = Math.min(...scores);

  // All teams tied → all players happy
  if (maxScore === minScore) {
    players.forEach((player) => map.set(player.playerId, 'happy'));
    return map;
  }

  // Top and bottom groups
  const topTeamIds = new Set(
    teamScores.filter((t) => t.score === maxScore).map((t) => t.teamId)
  );
  const bottomTeamIds = new Set(
    maxScore > minScore
      ? teamScores.filter((t) => t.score === minScore).map((t) => t.teamId)
      : []
  );

  // Assign top and bottom emotions
  topTeamIds.forEach((teamId) => {
    players
      .filter((p) => p.teamId === teamId)
      .forEach((player) => map.set(player.playerId, 'surprised'));
  });
  bottomTeamIds.forEach((teamId) => {
    players
      .filter((p) => p.teamId === teamId)
      .forEach((player) => map.set(player.playerId, 'angry'));
  });

  // Remaining teams → split halves (prefer happy on odd)
  const remaining = teamScores
    .filter((t) => !topTeamIds.has(t.teamId) && !bottomTeamIds.has(t.teamId))
    .sort((a, b) => b.score - a.score);
  const n = remaining.length;
  const topHalfCount = Math.ceil(n / 2);
  remaining.forEach((teamScore, idx) => {
    const emotion = idx < topHalfCount ? 'happy' : 'sad';
    players
      .filter((p) => p.teamId === teamScore.teamId)
      .forEach((player) => map.set(player.playerId, emotion));
  });

  // Players not on any team → happy (fallback)
  players.forEach((player) => {
    if (!player.teamId && !map.has(player.playerId)) {
      map.set(player.playerId, 'happy');
    }
  });

  return map;
}
